import { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Loader2, X, Image as ImageIcon, Plus, Camera, FileImage } from 'lucide-react';
import { trpc } from '~/api/trpc';
import { Button } from '~/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/components/ui/dialog';
import { Textarea } from '~/components/ui/textarea';
import { Label } from '~/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { AutoComplete } from '~/components/ui/autocomplete';
import { cn } from '~/lib/utils';
import MedicalImageCapture from './MedicalImageCapture';

const ALLOWED_APPOINTMENT_STATUSES = ['SCHEDULED', 'COMPLETED', 'REQUESTED'];
const MAX_PHOTOS = 5;

const formatAppointmentOption = (appointment: {
    steps?: { dateFrom?: Date | string | null }[];
    originalTreatmentVariants?: { treatmentVariant?: { name?: string } | null }[];
}) => {
    const firstStep = appointment.steps?.[0];
    const dateStr = firstStep?.dateFrom
        ? format(new Date(firstStep.dateFrom), 'dd.MM.yyyy HH:mm', { locale: de })
        : 'Kein Datum';
    const treatments = appointment.originalTreatmentVariants
        ?.map((tv) => tv.treatmentVariant?.name)
        .filter(Boolean)
        .join(', ') || 'Keine Behandlung';
    return `${dateStr} - ${treatments}`;
};

type PhotoEntry = {
    id: string;
    file: File;
    previewUrl: string;
    bodyRegionId: string;
    description: string;
    imageType: string;
};

type AddImageDialogProps = {
    customerId: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
    medicalRecordTreatmentId?: string;
    preselectedAppointmentId?: string | null;
};

const AddImageDialog = ({
    customerId,
    open,
    onOpenChange,
    onSuccess,
    medicalRecordTreatmentId,
    preselectedAppointmentId,
}: AddImageDialogProps) => {
    const [mode, setMode] = useState<null | 'camera' | 'upload'>(null);
    const [photos, setPhotos] = useState<PhotoEntry[]>([]);
    const [activePhotoIndex, setActivePhotoIndex] = useState(0);
    const [globalImageType, setGlobalImageType] = useState<string>('OTHER');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<string | null>(null);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
    const [cameraAutoOpen, setCameraAutoOpen] = useState(false);
    const [regionSearch, setRegionSearch] = useState('');

    const uploadInputRef = useRef<HTMLInputElement>(null);

    const utils = trpc.useUtils();

    const { data: appointments } = trpc.appointments.getByCustomerId.useQuery(
        { customerId },
        { enabled: open }
    );

    const { data: treatmentRegions } = trpc.medicalRecord.getTreatmentRegions.useQuery(
        undefined,
        { enabled: open }
    );

    const regionItems = useMemo(() =>
        (treatmentRegions ?? [])
            .filter((r) => !regionSearch || r.name.toLowerCase().includes(regionSearch.toLowerCase()))
            .map((r) => ({ value: r.id, label: r.name })),
        [treatmentRegions, regionSearch]
    );

    const filteredAppointments = useMemo(
        () => (appointments ?? []).filter((a) => ALLOWED_APPOINTMENT_STATUSES.includes(a.status)),
        [appointments]
    );

    const hasAutoSelectedRef = useRef(false);
    useEffect(() => {
        if (!open) {
            hasAutoSelectedRef.current = false;
            return;
        }
        if (hasAutoSelectedRef.current) return;
        if (preselectedAppointmentId) {
            hasAutoSelectedRef.current = true;
            setSelectedAppointmentId(preselectedAppointmentId);
            return;
        }
        if (filteredAppointments.length === 0) return;

        const now = Date.now();
        const candidates = filteredAppointments
            .map((a) => ({ id: a.id, dateFrom: a.steps?.[0]?.dateFrom ? new Date(a.steps[0].dateFrom).getTime() : null }))
            .filter((a): a is { id: string; dateFrom: number } => a.dateFrom != null && a.dateFrom <= now)
            .sort((a, b) => b.dateFrom - a.dateFrom);

        if (candidates.length > 0) {
            hasAutoSelectedRef.current = true;
            setSelectedAppointmentId(candidates[0].id);
        }
    }, [open, filteredAppointments, preselectedAppointmentId]);

    const uploadImage = trpc.medicalRecord.uploadImage.useMutation();

    const addPhoto = (file: File) => {
        if (photos.length >= MAX_PHOTOS) return;
        const entry: PhotoEntry = {
            id: crypto.randomUUID(),
            file,
            previewUrl: URL.createObjectURL(file),
            bodyRegionId: '',
            description: '',
            imageType: globalImageType,
        };
        setPhotos((prev) => {
            const next = [...prev, entry];
            setActivePhotoIndex(next.length - 1);
            return next;
        });
    };

    const removePhoto = (index: number) => {
        setPhotos((prev) => {
            URL.revokeObjectURL(prev[index].previewUrl);
            const next = prev.filter((_, i) => i !== index);
            if (next.length === 0) {
                setMode(null);
            }
            return next;
        });
        setActivePhotoIndex((prev) => {
            if (photos.length <= 1) return 0;
            if (prev >= photos.length - 1) return Math.max(0, photos.length - 2);
            if (index < prev) return prev - 1;
            if (index === prev) return Math.min(prev, photos.length - 2);
            return prev;
        });
    };

    const updateActivePhoto = (field: keyof Pick<PhotoEntry, 'bodyRegionId' | 'description' | 'imageType'>, value: string) => {
        setPhotos((prev) =>
            prev.map((p, i) => (i === activePhotoIndex ? { ...p, [field]: value } : p))
        );
    };

    const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setMode('upload');

        const remaining = MAX_PHOTOS - photos.length;
        const filesToAdd = Array.from(files).slice(0, remaining);

        for (const file of filesToAdd) {
            const entry: PhotoEntry = {
                id: crypto.randomUUID(),
                file,
                previewUrl: URL.createObjectURL(file),
                bodyRegionId: '',
                description: '',
                imageType: globalImageType,
            };
            setPhotos((prev) => {
                const next = [...prev, entry];
                setActivePhotoIndex(next.length - 1);
                return next;
            });
        }

        e.target.value = '';
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
            reader.readAsDataURL(file);
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedAppointmentId) {
            toast.error('Bitte wählen Sie einen Termin aus');
            return;
        }

        if (photos.length === 0) {
            toast.error('Bitte wählen Sie mindestens ein Bild aus');
            return;
        }

        setIsUploading(true);

        try {
            for (let i = 0; i < photos.length; i++) {
                const photo = photos[i];
                setUploadProgress(`${i + 1}/${photos.length}`);

                const imageBase64 = await fileToBase64(photo.file);

                await uploadImage.mutateAsync({
                    customerId,
                    imageBase64,
                    fileName: photo.file.name,
                    bodyRegionId: photo.bodyRegionId || undefined,
                    description: photo.description.trim() || undefined,
                    imageType: photo.imageType as 'BEFORE' | 'AFTER' | 'OTHER' | 'COMPLICATION',
                    medicalRecordTreatmentId,
                    appointmentId: selectedAppointmentId || undefined,
                });
            }

            utils.medicalRecord.getByCustomerId.invalidate({ customerId });
            toast.success(
                photos.length === 1
                    ? 'Bild wurde hochgeladen'
                    : `${photos.length} Bilder wurden hochgeladen`
            );
            resetForm();
            onOpenChange(false);
            onSuccess?.();
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(`Fehler beim Hochladen: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
        } finally {
            setIsUploading(false);
            setUploadProgress(null);
        }
    };

    const resetForm = () => {
        photos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        setPhotos([]);
        setActivePhotoIndex(0);
        setGlobalImageType('OTHER');
        setSelectedAppointmentId(null);
        setUploadProgress(null);
        setMode(null);
        setCameraAutoOpen(false);
        setRegionSearch('');
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) {
            resetForm();
        }
        onOpenChange(newOpen);
    };

    const activePhoto = photos[activePhotoIndex] ?? null;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>
                            {photos.length > 1
                                ? `Bilder hinzufügen (${photos.length}/${MAX_PHOTOS})`
                                : 'Bild hinzufügen'}
                        </DialogTitle>
                        <DialogDescription>
                            Laden Sie Bilder zur Patientenakte hoch oder nehmen Sie Fotos auf. Max. {MAX_PHOTOS} Bilder.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4 space-y-4">
                        {photos.length === 0 && mode === null ? (
                            <div>
                                <Label>Bild *</Label>
                                <div className="mt-2 border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                                    <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-sm text-gray-500 mb-4">
                                        Kein Bild ausgewählt
                                    </p>
                                    <div className="flex flex-col gap-2 items-center">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                uploadInputRef.current?.click();
                                            }}
                                            disabled={isUploading}
                                        >
                                            <FileImage className="h-4 w-4 mr-2" />
                                            Dateien auswählen
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setMode('camera');
                                                setCameraAutoOpen(true);
                                            }}
                                            disabled={isUploading}
                                        >
                                            <Camera className="h-4 w-4 mr-2" />
                                            Foto aufnehmen
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Photo strip */}
                                <div>
                                    <Label>Bilder ({photos.length}/{MAX_PHOTOS})</Label>
                                    <div className="mt-2 flex items-center gap-2 overflow-x-auto pb-1">
                                        {photos.map((photo, index) => (
                                            <div
                                                key={photo.id}
                                                className={cn(
                                                    'relative shrink-0 w-16 h-16 rounded-lg overflow-hidden cursor-pointer border-2 transition-all',
                                                    index === activePhotoIndex
                                                        ? 'border-primary ring-2 ring-primary/30'
                                                        : 'border-transparent hover:border-gray-300'
                                                )}
                                                onClick={() => setActivePhotoIndex(index)}
                                            >
                                                <img
                                                    src={photo.previewUrl}
                                                    alt={`Bild ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                                {!isUploading && (
                                                    <button
                                                        type="button"
                                                        className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removePhoto(index);
                                                        }}
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                )}
                                                <div className="absolute bottom-0.5 left-0.5 bg-black/60 text-white text-[10px] rounded px-1">
                                                    {index + 1}
                                                </div>
                                            </div>
                                        ))}
                                        {photos.length < MAX_PHOTOS && !isUploading && (
                                            mode === 'camera' ? (
                                                <button
                                                    type="button"
                                                    className="shrink-0 w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-primary hover:bg-muted/50 transition-colors"
                                                    onClick={() => setCameraAutoOpen(true)}
                                                >
                                                    <Plus className="h-5 w-5 text-muted-foreground" />
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="shrink-0 w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-primary hover:bg-muted/50 transition-colors"
                                                    onClick={() => uploadInputRef.current?.click()}
                                                >
                                                    <Plus className="h-5 w-5 text-muted-foreground" />
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>

                                {/* Active photo preview */}
                                {activePhoto && (
                                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                                        <img
                                            src={activePhoto.previewUrl}
                                            alt="Vorschau"
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {/* Appointment Selector */}
                        <div>
                            <Label>Termin *</Label>
                            <Select
                                value={selectedAppointmentId || ''}
                                onValueChange={(value) => setSelectedAppointmentId(value || null)}
                                disabled={isUploading}
                            >
                                <SelectTrigger className="mt-2">
                                    <SelectValue placeholder="Termin auswählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {filteredAppointments.map((appointment) => (
                                        <SelectItem key={appointment.id} value={appointment.id}>
                                            {formatAppointmentOption(appointment)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Global Image Type */}
                        <div>
                            <Label>Bildtyp *</Label>
                            <Select
                                value={activePhoto?.imageType ?? globalImageType}
                                onValueChange={(value) => {
                                    setGlobalImageType(value);
                                    if (activePhoto) {
                                        updateActivePhoto('imageType', value);
                                    }
                                }}
                                disabled={isUploading}
                            >
                                <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="BEFORE">Vorher-Bild</SelectItem>
                                    <SelectItem value="AFTER">Nachher-Bild</SelectItem>
                                    <SelectItem value="OTHER">Sonstiges</SelectItem>
                                    <SelectItem value="COMPLICATION">Komplikation</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Per-photo metadata (only shown when photos exist) */}
                        {activePhoto && (
                            <>
                                <div>
                                    <Label>
                                        Körperregion {photos.length > 1 ? `(Bild ${activePhotoIndex + 1})` : ''}
                                    </Label>
                                    <div className="mt-2">
                                        <AutoComplete
                                            selectedValue={activePhoto.bodyRegionId}
                                            onSelectedValueChange={(value) => updateActivePhoto('bodyRegionId', value)}
                                            searchValue={regionSearch}
                                            onSearchValueChange={setRegionSearch}
                                            items={regionItems}
                                            placeholder="Region suchen..."
                                            emptyMessage="Keine Region gefunden"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="image-description">
                                        Beschreibung {photos.length > 1 ? `(Bild ${activePhotoIndex + 1})` : ''}
                                    </Label>
                                    <Textarea
                                        id="image-description"
                                        value={activePhoto.description}
                                        onChange={(e) => updateActivePhoto('description', e.target.value)}
                                        placeholder="Optionale Beschreibung..."
                                        className="mt-2"
                                        disabled={isUploading}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleOpenChange(false)}
                            disabled={isUploading}
                        >
                            Abbrechen
                        </Button>
                        <Button type="submit" disabled={isUploading || photos.length === 0}>
                            {isUploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {isUploading && uploadProgress
                                ? `Hochladen ${uploadProgress}...`
                                : photos.length <= 1
                                    ? 'Bild hochladen'
                                    : `${photos.length} Bilder hochladen`}
                        </Button>
                    </DialogFooter>
                </form>

                {/* Hidden file input for upload mode */}
                <input
                    type="file"
                    ref={uploadInputRef}
                    onChange={handleFilesSelected}
                    accept="image/*"
                    multiple
                    className="hidden"
                />

                {/* Hidden MedicalImageCapture for camera mode (uses autoOpen + autoCapture) */}
                {mode === 'camera' && (
                    <div className="hidden">
                        <MedicalImageCapture
                            key={cameraAutoOpen ? 'open' : 'closed'}
                            onImageCaptured={(file) => {
                                addPhoto(file);
                                setCameraAutoOpen(false);
                            }}
                            autoCapture
                            autoOpen={cameraAutoOpen}
                            onCancelled={() => {
                                setCameraAutoOpen(false);
                                if (photos.length === 0) {
                                    setMode(null);
                                }
                            }}
                        />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AddImageDialog;
